import { Temporal } from "@js-temporal/polyfill";
import soft from "timezone-soft";
import { z } from "zod";
import { zodToJsonSchema } from "zod-to-json-schema";

interface Response {
  events: Event[];
}

function normalizeUrlForStorage(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://"))
    return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

export const PLATFORMS = [
  "instagram",
  "tiktok",
  "facebook",
  "twitter",
  "unknown",
] as const;
export const PlatformSchema = z.enum(PLATFORMS);
export type Platform = z.infer<typeof PlatformSchema>;

export const EventMetadataSchema = z.object({
  platform: PlatformSchema.default("unknown").describe(
    "The platform where this event was sourced from (e.g., instagram, tiktok, facebook, twitter, or unknown)",
  ),
  mentions: z
    .array(z.string())
    .optional()
    .describe(
      "Array of usernames (without @ symbol) mentioned in the post. The FIRST username must be the main author/organizer of the event.",
    ),
  sourceUrls: z
    .array(
      z.preprocess(
        (val) => (typeof val === "string" ? normalizeUrlForStorage(val) : val),
        z.string().url(),
      ),
    )
    .optional()
    .describe("Array of URLs to the original posts or related content"),
});
export type EventMetadata = z.infer<typeof EventMetadataSchema>;

export const AGE_RESTRICTIONS = ["all-ages", "18+", "21+", "unknown"] as const;
export const AgeRestrictionSchema = z.enum(AGE_RESTRICTIONS);
export type AgeRestriction = z.infer<typeof AgeRestrictionSchema>;

export const PRICE_TYPE = [
  "donation",
  "free",
  "notaflof",
  "paid",
  "unknown",
] as const;
export const PriceTypeSchema = z.enum(PRICE_TYPE);
export type PriceType = z.infer<typeof PriceTypeSchema>;

export const EVENT_CATEGORIES = [
  "arts",
  "business",
  "community",
  "culture",
  "education",
  "entertainment",
  "food",
  "health",
  "lifestyle",
  "literature",
  "music",
  "religion",
  "science",
  "sports",
  "tech",
  "unknown",
] as const;
export const EventCategorySchema = z.string();
export type EventCategory = z.infer<typeof EventCategorySchema>;

export const EVENT_TYPES = [
  "competition",
  "concert",
  "conference",
  "exhibition",
  "festival",
  "game",
  "meeting",
  "movie",
  "opening",
  "party",
  "performance",
  "seminar",
  "show",
  "unknown",
  "webinar",
  "workshop",
] as const;
export const EventTypeSchema = z.string();
export type EventType = z.infer<typeof EventTypeSchema>;

export const ACCESSIBILITY_TYPES = [
  "closedCaptioning",
  "masksRequired",
  "masksSuggested",
  "signLanguageInterpretation",
  "wheelchairAccessible",
] as const;
export const AccessibilityTypeSchema = z.enum(ACCESSIBILITY_TYPES);
export type AccessibilityType = z.infer<typeof AccessibilityTypeSchema>;
export const ACCESSIBILITY_TYPES_OPTIONS = [
  { value: "closedCaptioning", label: "Closed Captioning" },
  { value: "masksRequired", label: "Masks Required" },
  { value: "masksSuggested", label: "Masks Suggested" },
  {
    value: "signLanguageInterpretation",
    label: "Sign Language Interpretation",
  },
  { value: "wheelchairAccessible", label: "Wheelchair Accessible" },
];

export const EventMetadataSchemaLegacy = z.object({
  accessibility: z.array(AccessibilityTypeSchema).optional(),
  accessibilityNotes: z.string().optional(),
  ageRestriction: AgeRestrictionSchema.optional(),
  category: EventCategorySchema.optional(),
  mentions: z.array(z.string()).optional(),
  performers: z.array(z.string()).optional(),
  priceMax: z.number().optional(),
  priceMin: z.number().optional(),
  priceType: PriceTypeSchema.optional(),
  source: PlatformSchema.optional(),
  type: EventTypeSchema.optional(),
});
export type EventMetadataLegacy = z.infer<typeof EventMetadataSchemaLegacy>;

export const EventMetadataSchemaLoose = EventMetadataSchemaLegacy.extend({
  accessibility: z.array(z.string()).optional(),
  ageRestriction: z.string().optional(),
  category: z.string().optional(),
  priceType: z.string().optional(),
  source: z.string().optional(),
  type: z.string().optional(),
});
export type EventMetadataLoose = z.infer<typeof EventMetadataSchemaLoose>;

export const EventSchema = z.object({
  name: z
    .string()
    .describe(
      "The event's name. Be specific and include any subtitle or edition. Do not include the location.",
    ),
  description: z
    .string()
    .describe(
      "Short description of the event, its significance, and what attendees can expect. If included in the source text, include the cost, allowed ages, rsvp details, performers, speakers, and any known times. When mentioning times in the description, use casual 12-hour format (e.g., '2:30 PM' instead of '14:30').",
    ),
  shortDescription: z
    .string()
    .optional()
    .describe(
      "3-5 word plain phrase saying what kind of event this is (e.g. 'queer country dance night'). Lowercase except proper nouns. Don't repeat the event name, date, or venue.",
    ),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .describe("Start date in YYYY-MM-DD format."),
  startTime: z
    .string()
    .describe(
      "Start time in 24-hour format HH:MM:SS (e.g., 14:30:00 for 2:30 PM). Always include seconds, even if they're 00.",
    ),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .describe("End date in YYYY-MM-DD format."),
  endTime: z
    .string()
    .describe(
      "End time in 24-hour format HH:MM:SS (e.g., 14:30:00 for 2:30 PM). Always include seconds, even if they're 00.",
    ),
  hasExplicitYear: z
    .boolean()
    .optional()
    .describe(
      "Set to true ONLY if the source text or image explicitly states a 4-digit year for the event (e.g., 'March 15, 2028' or 'Summer 2027'). Omit or set to false in every other case, including when the year is merely implied by recency, context, or your own guess. When false, any placeholder year you pick for startDate/endDate will be discarded and replaced downstream with the soonest future year that matches the month and day.",
    ),
  isAllDay: z
    .boolean()
    .optional()
    .describe(
      "True when the source describes an event with no specific clock time. createEvent will normalize startTime to 00:00:00 and endTime to 23:59:59 in the event's timezone.",
    ),
  startTimeProvenance: z
    .enum(["stated", "assumed", "verified", "edited"])
    .optional(),
  endTimeProvenance: z
    .enum(["stated", "assumed", "verified", "edited"])
    .optional(),
  dateProvenance: z
    .enum(["stated", "assumed", "verified", "edited"])
    .optional(),
  timeZone: z.string().describe("Timezone in IANA format."),
  location: z.string().describe("Location of the event."),
});
export const EventWithMetadataSchema = EventSchema.extend({
  eventMetadata: EventMetadataSchema.optional(),
});
export type EventWithMetadata = z.infer<typeof EventWithMetadataSchema>;
export const EventsSchema = z.array(EventSchema);
export const EventsWithMetadataSchema = z.array(EventWithMetadataSchema);

export type Event = z.infer<typeof EventSchema>;

export const extractJsonFromResponse = (response: string) => {
  try {
    const start = response.indexOf("```json");
    const end = response.lastIndexOf("```");
    if (start === -1 || end === -1) {
      return JSON.parse(response) as Response;
    }
    const jsonString = response.slice(start + 7, end);
    return JSON.parse(jsonString) as Response;
  } catch (error) {
    console.error("An error occurred while parsing the JSON response:", error);
    return undefined;
  }
};

export const addCommonAddToCalendarProps = (events: EventWithMetadata[]) => {
  return events.map((event) => {
    return {
      options: [
        "Apple",
        "Google",
        "iCal",
        "Microsoft365",
        "MicrosoftTeams",
        "Outlook.com",
        "Yahoo",
      ] as
        | (
            | "Apple"
            | "Google"
            | "iCal"
            | "Microsoft365"
            | "MicrosoftTeams"
            | "Outlook.com"
            | "Yahoo"
          )[]
        | undefined,
      buttonStyle: "text" as const,
      name: event.name,
      description: event.description,
      location: event.location,
      startDate: event.startDate,
      endDate: event.endDate,
      startTime: event.startTime,
      endTime: event.endTime,
      timeZone: event.timeZone,
      eventMetadata: event.eventMetadata || undefined,
    };
  });
};

export const addCommonAddToCalendarPropsFromResponse = (response: string) => {
  const res = extractJsonFromResponse(response);
  if (!res) return undefined;
  const { events } = res;
  return addCommonAddToCalendarProps(events);
};

export const eventMetadataSchemaAsText = JSON.stringify(
  zodToJsonSchema(EventMetadataSchema),
);

export const systemMessage = (schema?: string) =>
  `You are an AI assistant that extracts calendar event details from text or images. Provide structured outputs in JSON format, strictly adhering to the provided schema. Non-conforming outputs will cause fatal errors.

  **Schema Adherence:**
  * **Only Valid Options**: Enum fields must only use values explicitly defined in the schema.
  * **Always Omit Unknowns:** Always completely omit fields if their values are undefined or cannot be reasonably inferred from the input. No placeholders, null, or undefined values.

  **Reasoning and Assumptions:**

  * Make reasonable assumptions when necessary, but prioritize facts and direct information from the input.
  * Acknowledge any uncertainties and avoid making unsupported statements.

  **Output Style:** Keep responses concise, clear, and relevant.

  **Critical Output Format:**
  * Return ONLY a single JSON object with no extra text, no markdown code fences, no backticks, and no explanations.
  * The response must be pure JSON that can be parsed directly without any preprocessing.
  * Do NOT wrap the JSON in markdown code blocks (no \`\`\`json or \`\`\`).

  ${
    schema
      ? `
    **Schema:** The schema is as follows:
    ${schema}
      `
      : ``
  }`;

export const getText = (date: string, timezone: string) => `# CONTEXT
The current date is ${date}, and the default timezone is ${timezone} unless specified otherwise.

## YOUR JOB
Below, I pasted a text or image from which to extract calendar event details.

You will
1. Identify details of the primary event mentioned in the text or image. Only the first event from multi-day events should be captured.
2. Remove the perspective or opinion from the input, focusing only on factual details.
3. Extract and format these details into a valid JSON response, strictly following the schema below.
4. Infer any missing information based on event context, type, or general conventions.
5. Write your JSON response by summarizing the event details from the provided data or your own inferred knowledge. Your response must be detailed, specific, and directly relevant to the JSON schema requirements.
6. Limit event to a single time within a <24 hour period. Late night events can extend into the next day.

**CRITICAL: Return ONLY pure JSON. No markdown code fences, no backticks, no extra text. The response must be a single JSON object that can be parsed directly.**

Stylistically write titles and descriptions in short, approachable, and professional language, like an editor of the Village Voice event section.
Stick to known facts, and be concise. Use proper capitalization for all fields.
No new adjectives/adverbs not in source text. No editorializing. No fluff. Nothing should be described as "engaging", "compelling", etc...
Avoid using phrases like 'join us,' 'come celebrate,' or any other invitations. Instead, maintain a neutral and descriptive tone. For example, instead of saying 'Join a family-friendly bike ride,' describe it as 'A family-friendly bike ride featuring murals, light installations, and a light-up dance party.'"
Use they/them pronouns when referring to people whose gender is not explicitly stated.
Do not mention event times in the description unless they provide extra context beyond start and end times.

## DATE AND TIME PARSING AND FORMATTING
The system using the output requires specific date and time formatting.

- There are no all-day events. Always provide start and end times.
- Events must be limited to a single time within a 24 hour period. Only the first event from multi-day events should be captured.
- Dates MUST be in the format YYYY-MM-DD (e.g., 2026-03-15).
- Times MUST be in 24-hour format HH:MM:SS (e.g., 14:30:00 for 2:30 PM).
- Always include seconds in the time, even if they're 00.
- Always provide both startTime and endTime.

**Year Inference (read carefully):**
- Set \`hasExplicitYear\` to true ONLY when the source text or image literally contains a 4-digit year tied to the event (e.g., "March 15, 2028", "Summer 2027", "NYE 2026/2027"). Recency, context, or your own best guess DO NOT count as explicit.
- When \`hasExplicitYear\` is false, the year you write into \`startDate\`/\`endDate\` does not matter — pick the current year as a placeholder. The system will replace it downstream with the soonest future year that matches the month and day, so DO NOT try to reason about whether the event has "already passed this year".
- When \`hasExplicitYear\` is true, use the exact year stated. Never invent a year just to satisfy recency.

** Time Inference (when a start or end time is not explicitly stated):**
- If start time is not explicitly stated, infer a reasonable start time based on the event type and context (e.g., 19:00:00 for an evening concert, 10:00:00 for a morning workshop).
- If end time is not explicitly stated, infer a reasonable duration based on the event type and context (e.g., 2 hours for a movie, 3 hours for a concert, etc.).
- Ensure the endDate is always provided and is either the same as or later than the startDate.
`;

export const getTextMetadata = () => `
# YOUR TASK
Extract social media attribution metadata.
Return JSON with exactly: platform, mentions, sourceUrls.

## platform (required)
- One of: "instagram" | "tiktok" | "facebook" | "twitter" | "unknown".
- Default to "unknown" unless platform is clearly identifiable from UI or a fully visible platform URL.
- If no usernames are extracted, platform MUST be "unknown".

## mentions (required)
- Array of complete usernames (no "@"), deduplicated.
- Extract ONLY from post content (caption/text/tags/profile author). Never from engagement: "Liked by...", "Followed by...", like counts, or follower lists.
- Exclude display names.
- Exclude any truncated handles. If a handle is directly followed by ellipsis characters ("..." or "…"), EXCLUDE it. Do not guess or complete it.
- Collab author headers (e.g., "user1 and user2...") always have a truncated second author → include only the fully visible first author.
- Order: author first; then other fully visible mentions. Shared story: [original creator, story sharer].
- If none, return [].

## sourceUrls (required)
- Any complete, valid URLs visible. If none, [].

## Output
Always return valid JSON as a pure JSON object with no markdown code fences, no backticks, and no extra text.

Example:
{ "platform": "instagram", "mentions": ["username1", "username2"], "sourceUrls": ["https://example.com"] }

Empty-case example:
{ "platform": "unknown", "mentions": [], "sourceUrls": [] }

Collab header example (truncation):
Input UI: "jaronheard and friendsofnoi..."
{ "platform": "instagram", "mentions": ["jaronheard"], "sourceUrls": [] }

**CRITICAL: Return ONLY pure JSON. No markdown code fences, no backticks, no extra text. The response must be a single JSON object that can be parsed directly.**

Common pitfalls (avoid):
- Extracting from "Liked by..." or "Followed by...".
- Including truncated or display names.
- Guessing platform; when unsure, use "unknown".
`;

export const formatOffsetAsIANASoft = (offset: string) => {
  const timezone = soft(offset)[0];
  return timezone?.iana || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getPrompt = (
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  now?: Temporal.ZonedDateTime,
) => {
  const timezoneIANA = formatOffsetAsIANASoft(timezone);
  const zoned = now ?? Temporal.Now.instant().toZonedDateTimeISO(timezoneIANA);
  const date = zoned.toPlainDate().toString();

  return {
    text: getText(date, timezoneIANA),
    textMetadata: getTextMetadata(),
    version: "v2026.04.10.1",
  };
};

export const getSystemMessage = () => {
  return {
    text: systemMessage(),
    version: "v2026.04.10.1",
  };
};

export const getSystemMessageMetadata = () => {
  return {
    text: systemMessage(eventMetadataSchemaAsText),
    version: "v2026.04.10.1",
  };
};
